import React, {PropTypes as pt} from 'react';
import {connect} from 'react-redux';
import moment from 'moment-timezone';
import {Input, Select} from '../index';
import utils from '../../../api/utils';
import {getErrors, getFormData, getNonFieldError} from './selectors';
import {createAction} from '../../../utils/actions';
import {LESSON_START_VALUE_CHANGED, postStartChange} from './actions';
import DateTimePicker from '../components/form/DateTimePicker';
import {getTimezoneOptions} from '../dateTime';

class Component extends React.Component {
    static propTypes = {
        onChange: pt.func.isRequired,
        onSubmit: pt.func.isRequired,
        initial: pt.string,
        initialDate: pt.string,
        initialTime: pt.string,
        lessonId: pt.string,
        data: pt.shape({}),
        errors: pt.shape({}),
        nonFieldError: pt.string,
        readOnly: pt.bool,
        hasStartTime: pt.bool,
        teacherTimezone: pt.string,
        studentTimezone: pt.string,
    };

    constructor(props) {
        super(props);
        this.state = {
            isSubmitting: false,
            cetFormatted: '',
            timezone: props.teacherTimezone || 'Europe/Moscow',
            timezoneSaving: false,
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.isFormValid = this.isFormValid.bind(this);
        this.handleCETFormattedChange = this.handleCETFormattedChange.bind(this);
        this.handleTimezoneChange = this.handleTimezoneChange.bind(this);
    }

    handleCETFormattedChange(cetFormatted) {
        this.setState({ cetFormatted });
    }

    componentDidMount() {
        if (this.props.initial) {
            this.props.onChange('new', this.props.initial);
            // Initialize CET formatted string
            const cetFormatted = DateTimePicker.formatLocal(this.props.initial, this.state.timezone);
            if (cetFormatted) {
                this.setState({ cetFormatted });
            }
        }
        if (!this.props.hasStartTime && (!this.props.data.reason || !this.props.data.reason.trim())) {
            this.props.onChange('reason', 'Initial lesson start');
        }
        this.loadTimezone();
    }

    componentDidUpdate(prevProps) {
        // Only update if initial changed AND it's different from current data.new
        // This prevents infinite update loops
        if (prevProps.initial !== this.props.initial) {
            const currentValue = this.props.data && this.props.data.new;
            const newInitial = this.props.initial;
            
            // Only update if the new initial value is different from current value
            if (newInitial && newInitial !== currentValue) {
                this.props.onChange('new', newInitial);
                // Update CET formatted string
                const cetFormatted = DateTimePicker.formatLocal(newInitial, this.state.timezone);
                if (cetFormatted) {
                    this.setState({ cetFormatted });
                }
            } else if (!newInitial && currentValue) {
                this.props.onChange('new', '');
                this.setState({ cetFormatted: '' });
            }
        }
        
        // Update CET formatted string when data.new changes
        if (prevProps.data && prevProps.data.new !== this.props.data.new) {
            const cetFormatted = DateTimePicker.formatLocal(this.props.data.new, this.state.timezone);
            this.setState({ cetFormatted: cetFormatted || '' });
        }
    }

    handleSubmit(e) {
        try {
            e.preventDefault();
            e.stopPropagation(); // Prevent event bubbling for Opera compatibility
        } catch (err) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('LessonStartForm handleSubmit preventDefault:', err);
            }
        }

        if (this.props.readOnly || this.state.isSubmitting || !this.props.lessonId) {
            return false;
        }

        if (!this.isFormValid()) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('Form validation failed:', {
                    new: this.props.data.new,
                    initiator: this.props.data.initiator,
                    reason: this.props.data.reason
                });
            }
            return false;
        }

        this.setState({ isSubmitting: true });

        const self = this;
        this.props.onSubmit({ lessonID: this.props.lessonId }, this.props.data)
            .then(function () {
                setTimeout(function () {
                    window.location.reload();
                }, 100);
            })
            .catch(function (error) {
                if (typeof console !== 'undefined' && console.error) {
                    console.error('Lesson start form submission error:', error);
                }
                self.setState({ isSubmitting: false });
                const message = (error && (error.getMessage ? error.getMessage() : error.message)) || 'Failed to save lesson start time. Please try again.';
                if (window.toastr) {
                    window.toastr.error(message, 'Error');
                } else {
                    alert(message);
                }
            });

        return false; // Prevent default form submission
    }

    loadTimezone() {
        utils.get(`${BASE_API_URL}/profile.json`)
            .then((profile) => {
                const timezone = (profile && profile.timezone) || this.props.teacherTimezone || 'Europe/Moscow';
                const value = (this.props.data && this.props.data.new) || this.props.initial;
                this.setState({
                    timezone,
                    cetFormatted: DateTimePicker.formatLocal(value, timezone) || '',
                });
            })
            .catch(() => {
                const timezone = this.props.teacherTimezone || 'Europe/Moscow';
                const value = (this.props.data && this.props.data.new) || this.props.initial;
                this.setState({
                    timezone,
                    cetFormatted: DateTimePicker.formatLocal(value, timezone) || '',
                });
            });
    }

    handleTimezoneChange(name, timezone) {
        if (!timezone || timezone === this.state.timezone) {
            return;
        }
        this.setState({ timezoneSaving: true, timezone });
        utils.patch(`${BASE_API_URL}/profile.json`, { timezone })
            .then(() => {
                this.setState({ timezoneSaving: false });
                if (window.toastr) {
                    window.toastr.success('Timezone updated');
                }
            })
            .catch(() => {
                this.setState({ timezoneSaving: false });
                if (window.toastr) {
                    window.toastr.error('Failed to update timezone');
                }
            });
    }

    getDisplayedTimes() {
        const baseDate = (this.props.data && this.props.data.new) || this.props.initial;
        if (!baseDate) {
            return [];
        }
        const studentTimezone = this.props.studentTimezone || 'Europe/Moscow';
        const teacherTimezone = this.state.timezone || this.props.teacherTimezone || 'Europe/Moscow';
        return [
            {
                label: 'Your local',
                value: moment(baseDate).tz(teacherTimezone).locale('en').format('dddd, YYYY-MM-DD HH:mm') + ' (' + teacherTimezone + ')',
            },
            {
                label: 'Student local',
                value: moment(baseDate).tz(studentTimezone).locale('en').format('dddd, YYYY-MM-DD HH:mm') + ' (' + studentTimezone + ')',
            },
        ];
    }

    isFormValid() {
        const { data } = this.props;
        // More robust validation for Opera compatibility
        const hasNew = data.new && (typeof data.new === 'string' ? data.new.trim() : data.new);
        const hasInitiator = data.initiator !== undefined && 
                            data.initiator !== null && 
                            data.initiator !== '' &&
                            String(data.initiator).trim() !== '';
        const hasReason = data.reason && (typeof data.reason === 'string' ? data.reason.trim() : data.reason);
        
        return hasNew && hasInitiator && hasReason;
    }

    render() {
        const { data, onChange, errors, nonFieldError, readOnly, initialDate, initialTime, initial } = this.props;
        const effectiveValue = (data.new != null && String(data.new).trim() !== '') ? data.new : (initial || '');
        const { isSubmitting, timezoneSaving } = this.state;
        const isValid = this.isFormValid();
        const disabled = readOnly || isSubmitting;
        const hasFieldErrors = errors && Object.keys(errors).length > 0;
        const displayedTimes = this.getDisplayedTimes();

        return (
            <form onSubmit={this.handleSubmit}>
                {(nonFieldError || hasFieldErrors) && (
                    <div className="alert alert-danger" role="alert">
                        {nonFieldError || 'Исправьте ошибки в полях ниже (поля с ошибками отмечены красным).'}
                    </div>
                )}
                <div className="form-group">
                    <label className="control-label">
                        {this.state.cetFormatted ? (
                            <span>Date and time <strong>{this.state.cetFormatted}</strong> <span className="text-danger">*</span></span>
                        ) : (
                            <span>Date and time <span className="text-danger">*</span></span>
                        )}
                    </label>
                    <DateTimePicker
                        value={effectiveValue}
                        initialDate={initialDate}
                        initialTime={initialTime}
                        disabled={disabled}
                        onChange={(value) => onChange('new', value)}
                        onCETFormattedChange={this.handleCETFormattedChange}
                        error={errors.new}
                        previewTimezone={this.state.timezone}
                    />
                </div>

                <Select
                    id="lsf-timezone"
                    name="timezone"
                    options={TIMEZONE_OPTIONS}
                    value={this.state.timezone}
                    label="Your timezone"
                    onChange={this.handleTimezoneChange}
                    hasEmpty={false}
                    horizontal={false}
                    disabled={disabled || timezoneSaving}
                />

                {displayedTimes.length > 0 && (
                    <div className="alert alert-info">
                        {displayedTimes.map(item => (
                            <div key={item.label}><strong>{item.label}:</strong> {item.value}</div>
                        ))}
                    </div>
                )}

                <Select
                    id="lsf-initiator"
                    name="initiator"
                    options={INITIATOR_OPTIONS}
                    value={data.initiator}
                    label="Initiator"
                    onChange={onChange}
                    hasEmpty={false}
                    horizontal={false}
                    disabled={disabled}
                    error={errors.initiator}
                />

                <Input
                    element="textarea"
                    label="Comment *"
                    onChange={onChange}
                    id="lsf-reason"
                    name="reason"
                    rows={5}
                    horizontal={false}
                    value={data.reason}
                    error={errors.reason}
                    disabled={disabled}
                />

                <div className="text-right">
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={disabled || !isValid}
                    >
                        {isSubmitting ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </form>
        );
    }
}

const INITIATOR_OPTIONS = [
    { key: '1', value: 'Teacher' },
    { key: '0', value: 'Student' },
];

const TIMEZONE_OPTIONS = getTimezoneOptions();

const LessonStartForm = connect(
    state => ({
        data: getFormData(state),
        errors: getErrors(state),
        nonFieldError: getNonFieldError(state),
    }),
    dispatch => ({
        onChange: (name, value) => dispatch(createAction(LESSON_START_VALUE_CHANGED, { name, value })),
        onSubmit: (lessonId, data) => dispatch(postStartChange(lessonId, data)),
    })
)(Component);

export default LessonStartForm;



// WEBPACK FOOTER //
// ./src/incab/common/LessonStartForm/component.js